import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Clock,
  ArrowUpRight,
  Rss,
  Send,
  BookOpen,
  MapPin,
  Stethoscope,
  Users,
  Briefcase,
  Star,
  Archive,
  Newspaper,
} from "lucide-react";
import { PageHero, SectionEyebrow } from "@/components/ui-bits";
import { BLOG_ARTICLES, BLOG_CATEGORIES } from "@/data/blogArticles";
import { CURRENT_ISSUE, NEWSLETTER_META } from "@/data/newsletterIssues";
import { NewsletterSignupModal } from "@/components/NewsletterSignupModal";
import { PermanentDisclaimer } from "@/components/NewsletterDisclaimer";
import { useAuth } from "@/context/AuthContext";

const SITE_URL = "https://ogonotary.com";

// Map existing blog slugs to the new hub filter taxonomy while preserving each
// article's original category label on its own card.
const HUB_FILTERS = [
  { id: "all", label: "All" },
  { id: "newsletter", label: "Newsletter" },
  { id: "notary-news", label: "Notary News" },
  { id: "service-education", label: "Service Education" },
  { id: "business-growth", label: "Business Growth" },
  { id: "resources", label: "Resources" },
];

const ARTICLE_HUB_TAGS = {
  "top-10-mobile-notary-field-kit-essentials": "resources",
  "5-signing-table-mistakes-that-cost-notaries-their-reputation":
    "service-education",
  "elder-care-notary-ethics-when-to-walk-away": "service-education",
  "ron-in-florida-what-signing-agents-should-know-2026": "notary-news",
  "building-a-referral-network-that-feeds-your-notary-business":
    "business-growth",
  "glba-compliance-plain-english-signing-agents": "service-education",
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

const SECTION_ICONS = [Newspaper, MapPin, Stethoscope, Users, Briefcase];

export default function BlogPage() {
  const canonical = `${SITE_URL}/blog`;
  const [signupOpen, setSignupOpen] = React.useState(false);
  const [filter, setFilter] = React.useState("all");
  const navigate = useNavigate();
  const { isAuthed } = useAuth();

  const filteredArticles =
    filter === "all"
      ? BLOG_ARTICLES
      : BLOG_ARTICLES.filter(
          (a) => ARTICLE_HUB_TAGS[a.slug] === filter
        );

  function handleGetFreeIssue() {
    if (isAuthed) {
      navigate(`/newsletter/${CURRENT_ISSUE.slug}`);
    } else {
      setSignupOpen(true);
    }
  }

  return (
    <>
      <Helmet>
        <title>Notary Blog and Weekly Newsletter | OGO Notary</title>
        <meta
          name="description"
          content="Read the OGO Weekly Notary Brief for national notary developments, state-specific guidance, specialized service education, client-growth strategies, and practical notary business information."
        />
        <meta
          name="keywords"
          content="notary newsletter, weekly notary brief, notary blog, mobile notary tips, signing agent newsletter, RON notary, notary field kit, notary business, OGO Notary"
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Notary Blog and Weekly Newsletter | OGO Notary"
        />
        <meta
          property="og:description"
          content="Weekly notary developments, state spotlights, service education, and small-business guidance."
        />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <PageHero
        eyebrow="OGO Blog & Newsletter"
        title="Make Our Notary Life Easier."
        lead="Weekly national and state notary developments, service education, client-growth ideas, and practical small-business guidance — plus every article from the OGO archive. What We Do Matters — and informed notaries are better prepared to serve."
      />

      {/* === NEWSLETTER FEATURED CARD === */}
      <section className="py-14 sm:py-20 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <SectionEyebrow className="mb-4">The Weekly Publication</SectionEyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight max-w-3xl">
            OGO Weekly Notary Brief
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed text-base sm:text-lg max-w-3xl">
            Stay informed, strengthen your services, and build a better notary
            business. The OGO Weekly Notary Brief brings together important
            notary developments, practical service guidance, client-development
            ideas, and small-business education in one concise weekly
            publication.
          </p>
          <p className="mt-3 text-[#D4AF37] text-sm font-semibold tracking-wide max-w-3xl">
            What We Do Matters — and informed notaries are better prepared to serve.
          </p>

          <div
            data-testid="newsletter-featured-card"
            className="mt-10 relative overflow-hidden border border-[#D4AF37]/50 bg-gradient-to-br from-[#1A2B45] via-[#142339] to-[#0F1B2E]"
          >
            <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#D4AF37]/[0.06] blur-3xl pointer-events-none" />
            <div className="relative grid lg:grid-cols-12 gap-0">
              <div className="lg:col-span-5 bg-[#0B1120] border-r border-white/10 flex items-center justify-center p-8">
                <img
                  src={CURRENT_ISSUE.cover}
                  alt={CURRENT_ISSUE.coverAlt}
                  className="w-full aspect-[4/3] object-cover border border-[#D4AF37]/40"
                  loading="lazy"
                />
              </div>
              <div className="lg:col-span-7 p-8 lg:p-12">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-3">
                  Free · Five Sections · Weekly
                </div>
                <h3 className="font-serif-display text-2xl sm:text-3xl text-white leading-tight mb-4">
                  OGO Weekly Notary Brief
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  A free five-part weekly briefing created for working notaries,
                  new notary entrepreneurs, signing agents, and professionals
                  building specialized notarial services.
                </p>
                <ul className="space-y-3 mb-8">
                  {CURRENT_ISSUE.sections.map((s, i) => {
                    const Icon = SECTION_ICONS[i] || Star;
                    return (
                      <li
                        key={i}
                        data-testid={`newsletter-section-${i}`}
                        className="flex items-start gap-3"
                      >
                        <span className="w-8 h-8 shrink-0 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
                          <Icon className="w-4 h-4" />
                        </span>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                            {s.kicker}
                          </div>
                          <div className="text-slate-200 text-sm leading-snug">
                            {s.headline}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={handleGetFreeIssue}
                    data-testid="newsletter-get-free-issue-cta"
                    className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] px-6 py-4 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
                  >
                    <Send className="w-4 h-4" />
                    Get the Free Issue
                  </button>
                  <Link
                    to="/newsletter"
                    data-testid="newsletter-archive-link"
                    className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#D4AF37] text-slate-200 hover:text-[#D4AF37] px-6 py-4 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
                  >
                    <Archive className="w-4 h-4" />
                    {isAuthed ? "View Newsletter Archive" : "Already a member? View Archive"}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === BLOG SECTION === */}
      <section className="py-16 sm:py-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
            <SectionEyebrow>From the OGO Blog</SectionEyebrow>
            <div
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-400"
              data-testid="blog-count"
            >
              <Rss className="w-3.5 h-3.5 text-[#D4AF37]" />
              {filteredArticles.length} article
              {filteredArticles.length === 1 ? "" : "s"}
            </div>
          </div>

          <div
            data-testid="blog-filter-chips"
            className="flex flex-wrap gap-2 mb-10"
          >
            {HUB_FILTERS.map((f) => {
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  data-testid={`blog-filter-${f.id}`}
                  className={`text-[11px] uppercase tracking-[0.22em] font-bold px-4 py-2 border transition-colors ${
                    active
                      ? "bg-[#D4AF37] text-[#142339] border-[#D4AF37]"
                      : "bg-transparent text-slate-300 border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          {filteredArticles.length === 0 ? (
            <div className="text-slate-400 text-sm py-16 text-center border border-dashed border-white/10">
              No articles yet in this category — check back soon.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          )}
        </div>
      </section>

      <PermanentDisclaimer />

      <NewsletterSignupModal
        open={signupOpen}
        onClose={() => setSignupOpen(false)}
      />
    </>
  );
}

function ArticleCard({ article }) {
  const cat = BLOG_CATEGORIES[article.category];
  return (
    <Link
      to={`/blog/${article.slug}`}
      data-testid={`blog-card-${article.slug}`}
      className="group relative bg-[#1A2B45] border border-white/10 hover:border-[#D4AF37]/60 transition-colors flex flex-col overflow-hidden"
    >
      {article.heroImage && (
        <div className="relative aspect-[16/9] overflow-hidden border-b border-white/10">
          <img
            src={article.heroImage}
            alt={article.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A2E] via-transparent to-transparent" />
        </div>
      )}
      <div className="p-6 sm:p-7 flex flex-col flex-1">
        <div className="flex items-center gap-3 mb-4 text-[10px] uppercase tracking-[0.22em]">
          {cat && <span className={cat.color}>{cat.label}</span>}
          <span className="text-slate-500">·</span>
          <span className="inline-flex items-center gap-1 text-slate-400">
            <Clock className="w-3 h-3" />
            {article.readMinutes} min
          </span>
        </div>
        <h3 className="font-serif-display text-xl sm:text-2xl text-white leading-tight mb-3 group-hover:text-[#D4AF37] transition-colors">
          {article.title}
        </h3>
        <p className="text-slate-300 text-sm leading-relaxed line-clamp-3 mb-5">
          {article.excerpt}
        </p>
        <div className="mt-auto pt-4 border-t border-white/10 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
            {formatDate(article.publishedAt)}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
            Read Article <ArrowUpRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}
